import { Tabs, TabsContent, TabsList, TabsTrigger } from '@design-system';
import {
  EmailTab,
  LinkTab,
  FillIntakeFormTab,
  SelfTestIntakeFormTab,
  useInviteClientTrigger,
  type TabType,
} from '@/features/invite-client';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { PilotRestricted } from '@/shared/components/pilot-restricted';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import {
  useActivePilotProgram,
  useIsPilotRestricted,
  usePilotSelfPlanIntakeForm,
} from '@/shared/hooks/use-active-pilot-program';
import { useIsPerClientInviteLinkEnabled } from '@/shared/hooks/use-per-client-invite-link';

import { ClipboardPen, Link2, Mail } from 'lucide-react';

export function InviteClientTabs() {
  const { flags } = useFeatureFlags();
  const { inviteClientTab, setInviteClientTab } = useInviteClientTrigger();
  const isEdjPilot = useIsEdjPilotAdvisor();
  const pilotProgram = useActivePilotProgram();
  const hasSelfPlanIntakeForm = !!usePilotSelfPlanIntakeForm();
  const isInviteClientRestricted = useIsPilotRestricted('invite-client');
  const isIntakeFormRestricted = useIsPilotRestricted('intake-form');

  const hideEmailAndFillIntakeForm = flags?.hideInviteEmailAndFillIntakeForm;
  const isPerClientInviteLink = useIsPerClientInviteLinkEnabled();

  const isEmailTabAvailable = !isInviteClientRestricted || isPerClientInviteLink;
  const defaultTab = isEmailTabAvailable ? 'email' : 'link';
  const currentTab = inviteClientTab || defaultTab;

  if (hideEmailAndFillIntakeForm && !isEdjPilot && !isPerClientInviteLink) {
    return (
      <div className="mt-4 w-full">
        <LinkTab withHeader={false} />
      </div>
    );
  }

  // A program's advisors can land on a tab their program withholds — via a stale deep
  // link, or by switching programs — so clamp back to the always-available link tab.
  const allowedTabs: TabType[] = [
    'link',
    ...(isEmailTabAvailable ? (['email'] as TabType[]) : []),
    ...(hasSelfPlanIntakeForm
      ? (['self-test'] as TabType[])
      : isIntakeFormRestricted
        ? []
        : (['fill-form'] as TabType[])),
  ];
  const resolvedTab = pilotProgram && !allowedTabs.includes(currentTab) ? 'link' : currentTab;

  return (
    <Tabs
      value={resolvedTab}
      onValueChange={value => setInviteClientTab(value as TabType)}
      className="w-full"
    >
      <TabsList className="w-full">
        {isPerClientInviteLink ? (
          <TabsTrigger
            value="email"
            className="flex-1 gap-2"
          >
            <Mail className="hidden size-4 sm:block" />
            Prefillable Invite Link
          </TabsTrigger>
        ) : (
          <PilotRestricted capability="invite-client">
            <TabsTrigger
              value="email"
              className="flex-1 gap-2"
            >
              <Mail className="hidden size-4 sm:block" />
              Invite via Email
            </TabsTrigger>
          </PilotRestricted>
        )}
        <TabsTrigger
          value="link"
          className="flex-1 gap-2"
        >
          <Link2 className="hidden size-4 sm:block" />
          {isPerClientInviteLink ? 'Reusable Invite Link' : 'Share via link'}
        </TabsTrigger>
        {hasSelfPlanIntakeForm ? (
          <TabsTrigger
            value="self-test"
            className="flex-1 gap-2"
          >
            <ClipboardPen className="hidden size-4 sm:block" />
            Plan for Yourself
          </TabsTrigger>
        ) : (
          <PilotRestricted capability="intake-form">
            <TabsTrigger
              value="fill-form"
              className="flex-1 gap-2"
            >
              <ClipboardPen className="hidden size-4 sm:block" />
              Fill Intake Form
            </TabsTrigger>
          </PilotRestricted>
        )}
      </TabsList>
      <TabsContent
        value="email"
        className="mt-4"
      >
        <EmailTab />
      </TabsContent>
      <TabsContent
        value="link"
        className="mt-4"
      >
        <LinkTab />
      </TabsContent>
      {hasSelfPlanIntakeForm ? (
        <TabsContent
          value="self-test"
          className="mt-4"
        >
          <SelfTestIntakeFormTab />
        </TabsContent>
      ) : (
        <TabsContent
          value="fill-form"
          className="mt-4"
        >
          <FillIntakeFormTab />
        </TabsContent>
      )}
    </Tabs>
  );
}
